import { createTheme, Theme } from '@mui/material/styles';

import { getTypography } from './typography';
import { colors, darkColors } from './palette';

const getTheme = () => {
  const baseTheme = createTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: {
            main: colors.primary,
            contrastText: colors.white,
          },
          secondary: {
            main: colors.lightBlue,
          },
          background: {
            default: colors.white,
            paper: colors.white,
          },
          text: {
            primary: colors.richBlack,
            disabled: colors.darkBlue,
          },
        },
      },
      dark: {
        palette: {
          primary: {
            main: colors.primary,
            contrastText: colors.white,
          },
          secondary: {
            main: darkColors.lightBlue,
          },
          background: {
            default: darkColors.background,
            paper: darkColors.paper,
          },
          text: {
            primary: darkColors.richBlack,
            disabled: darkColors.darkBlue,
          },
        },
      },
    },
  });

  const theme = createTheme({
    ...baseTheme,
    typography: getTypography(baseTheme),
    components: {
      /**
       * MuiButtonBase
       */
      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
      },

      /**
       * MuiPaper
       */
      MuiPaper: {
        styleOverrides: {
          root: {
            padding: 16,
            borderRadius: 8,
          },
          elevation1: {
            boxShadow: '0px 2px 48px rgba(0, 0, 0, 0.04)',
          },
        },
      },

      /**
       * MuiSwitch
       */
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 40,
            height: 20,
            padding: 0,
          },
          switchBase: {
            padding: 0,
            margin: 2,
            transitionDuration: '300ms',
            '&.Mui-checked': {
              transform: 'translateX(20px)',
              color: '#fff',
              '& + .MuiSwitch-track': {
                backgroundColor: baseTheme.palette.primary.main,
                opacity: 1,
                border: 0,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
              },
            },
            '&.Mui-focusVisible .MuiSwitch-thumb': {
              color: '#33cf4d',
              border: '6px solid #fff',
            },
            '&.Mui-disabled .MuiSwitch-thumb': ({ theme }: { theme: Theme }) =>
              theme.applyStyles('light', {
                color: baseTheme.palette.grey[100],
              }) || {
                color: baseTheme.palette.grey[600],
              },
            '&.Mui-disabled + .MuiSwitch-track': ({
              theme,
            }: {
              theme: Theme;
            }) =>
              theme.applyStyles('light', {
                opacity: 0.7,
              }) || {
                opacity: 0.3,
              },
          },
          thumb: {
            boxSizing: 'border-box',
            width: 16,
            height: 16,
            boxShadow: 'none',
          },
          track: ({ theme }) => ({
            borderRadius: 26 / 2,
            ...theme.applyStyles('light', {
              backgroundColor: '#E9E9EA',
            }),
            ...theme.applyStyles('dark', {
              backgroundColor: '#39393D',
            }),
            opacity: 1,
            transition: baseTheme.transitions.create(['background-color'], {
              duration: 500,
            }),
          }),
        },
      },

      MuiLink: {
        defaultProps: {
          underline: 'none',
        },
      },

      MuiSelect: {
        styleOverrides: {
          select: ({ theme }) =>
            theme.applyStyles('light', {
              backgroundColor: 'white',
            }),
        },
        defaultProps: {
          size: 'small',
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            padding: 0,
          },
        },
      },

      MuiButtonGroup: {
        defaultProps: {
          disableElevation: true,
        },
      },

      MuiBackdrop: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: colors.richBlack + '99',
            ...theme.applyStyles('dark', {
              backgroundColor: '#000000' + '99',
            }),
          }),
        },
      },

      MuiRadio: {
        defaultProps: {
          disableRipple: true,
          size: 'medium',
        },
        styleOverrides: {
          root: () => ({
            padding: 0,
            '& .MuiSvgIcon-root': {
              fontSize: 22,
            },
          }),
        },
      },
    },
  });

  return theme;
};

export const theme = getTheme();
